import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const AXES = [
  { key: 'priceLevel', label: 'Price Level' },
  { key: 'volatility', label: 'Volatility' },
  { key: 'changeRate', label: 'Change Rate' },
  { key: 'spread', label: 'Spread' },
  { key: 'serviceCoverage', label: 'Coverage' },
];

const SORT_OPTIONS = [
  { value: 'state', label: 'State Name' },
  { value: 'priceLevel', label: 'Price Level' },
  { value: 'volatility', label: 'Volatility' },
  { value: 'addressCount', label: 'Address Count' },
];

const styles = {
  container: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    padding: '24px',
  },
  header: {
    marginBottom: '20px',
  },
  title: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#111827',
    margin: '0 0 4px 0',
  },
  subtitle: {
    fontSize: '14px',
    color: '#6b7280',
    margin: 0,
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '20px',
  },
  sortLabel: {
    fontSize: '13px',
    color: '#374151',
    fontWeight: 500,
  },
  sortSelect: {
    padding: '6px 10px',
    fontSize: '13px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    backgroundColor: '#fff',
    color: '#374151',
    cursor: 'pointer',
    outline: 'none',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '16px',
  },
  card: {
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    cursor: 'pointer',
    transition: 'box-shadow 0.2s ease',
    position: 'relative',
  },
  cardHover: {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  stateAbbr: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#111827',
  },
  badge: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#4b5563',
    backgroundColor: '#f3f4f6',
    borderRadius: '12px',
    padding: '2px 8px',
  },
  svgContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '12px',
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '4px 12px',
  },
  metricItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  metricLabel: {
    fontSize: '10px',
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  metricValue: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#374151',
  },
  tooltip: {
    position: 'fixed',
    backgroundColor: '#1f2937',
    color: '#f9fafb',
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '12px',
    pointerEvents: 'none',
    zIndex: 10000,
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
    maxWidth: '220px',
  },
  tooltipTitle: {
    fontWeight: 700,
    fontSize: '14px',
    marginBottom: '8px',
    borderBottom: '1px solid #374151',
    paddingBottom: '6px',
  },
  tooltipRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '3px',
  },
  tooltipLabel: {
    color: '#9ca3af',
    marginRight: '12px',
  },
  tooltipValue: {
    fontWeight: 600,
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#9ca3af',
    fontSize: '15px',
  },
};

function RadarChart({ metrics, color, size = 140 }) {
  const svgRef = useRef(null);

  useEffect(() => {
    if (!svgRef.current || !metrics) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const cx = size / 2;
    const cy = size / 2;
    const radius = (size / 2) - 20;
    const levels = 4;
    const angleSlice = (2 * Math.PI) / AXES.length;

    const g = svg
      .attr('width', size)
      .attr('height', size)
      .append('g')
      .attr('transform', `translate(${cx}, ${cy})`);

    // Draw concentric grid circles
    for (let level = 1; level <= levels; level++) {
      const r = (radius / levels) * level;
      g.append('circle')
        .attr('cx', 0)
        .attr('cy', 0)
        .attr('r', r)
        .attr('fill', 'none')
        .attr('stroke', '#e5e7eb')
        .attr('stroke-width', 0.8);
    }

    // Draw axis lines and labels
    AXES.forEach((axis, i) => {
      const angle = angleSlice * i - Math.PI / 2;
      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius;

      g.append('line')
        .attr('x1', 0)
        .attr('y1', 0)
        .attr('x2', x)
        .attr('y2', y)
        .attr('stroke', '#d1d5db')
        .attr('stroke-width', 0.8);

      const labelX = Math.cos(angle) * (radius + 12);
      const labelY = Math.sin(angle) * (radius + 12);

      g.append('text')
        .attr('x', labelX)
        .attr('y', labelY)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'central')
        .attr('font-size', '7px')
        .attr('fill', '#9ca3af')
        .text(axis.label.split(' ').pop());
    });

    // Build data polygon points
    const points = AXES.map((axis, i) => {
      const value = metrics[axis.key] || 0;
      const angle = angleSlice * i - Math.PI / 2;
      return {
        x: Math.cos(angle) * radius * value,
        y: Math.sin(angle) * radius * value,
      };
    });

    const lineGenerator = d3
      .lineRadial()
      .radius((d, i) => radius * (metrics[AXES[i].key] || 0))
      .angle((d, i) => angleSlice * i)
      .curve(d3.curveLinearClosed);

    const dataForLine = AXES.map((_, i) => i);

    // Fill polygon
    g.append('path')
      .datum(dataForLine)
      .attr('d', lineGenerator)
      .attr('fill', color)
      .attr('fill-opacity', 0.2)
      .attr('stroke', color)
      .attr('stroke-width', 1.5)
      .attr('transform', `rotate(-90)`);

    // Data points
    points.forEach((pt) => {
      g.append('circle')
        .attr('cx', pt.x)
        .attr('cy', pt.y)
        .attr('r', 2.5)
        .attr('fill', color)
        .attr('stroke', '#fff')
        .attr('stroke-width', 1);
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [metrics, color, size]);

  return <svg ref={svgRef} />;
}

function formatMetric(key, value) {
  if (value == null) return '--';
  switch (key) {
    case 'avgPrice':
    case 'spread':
      return `$${value.toFixed(2)}`;
    case 'volatility':
      return value.toFixed(2);
    case 'changePercent':
      return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
    case 'coveragePercent':
      return `${value}%`;
    default:
      return String(value);
  }
}

const RAW_METRIC_DISPLAY = [
  { key: 'avgPrice', label: 'Avg Price' },
  { key: 'volatility', label: 'Volatility' },
  { key: 'changePercent', label: 'Change' },
  { key: 'spread', label: 'Spread' },
  { key: 'coveragePercent', label: 'Coverage' },
];

export default function StatePriceProfile({ data }) {
  const [sortBy, setSortBy] = useState('state');
  const [tooltip, setTooltip] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);

  const sortedStates = useMemo(() => {
    if (!data || !data.states || data.states.length === 0) return [];

    const states = [...data.states];

    states.sort((a, b) => {
      switch (sortBy) {
        case 'state':
          return a.state.localeCompare(b.state);
        case 'priceLevel':
          return (b.metrics.priceLevel || 0) - (a.metrics.priceLevel || 0);
        case 'volatility':
          return (b.metrics.volatility || 0) - (a.metrics.volatility || 0);
        case 'addressCount':
          return (b.addressCount || 0) - (a.addressCount || 0);
        default:
          return 0;
      }
    });

    return states;
  }, [data, sortBy]);

  const handleMouseEnter = (e, stateData) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      x: rect.right + 10,
      y: rect.top,
      stateData,
    });
    setHoveredCard(stateData.state);
  };

  const handleMouseLeave = () => {
    setTooltip(null);
    setHoveredCard(null);
  };

  if (!data || !data.states || data.states.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h3 style={styles.title}>State Pricing Profiles</h3>
          <p style={styles.subtitle}>Multi-dimensional pricing profile per state</p>
        </div>
        <div style={styles.emptyState}>No state pricing data available.</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>State Pricing Profiles</h3>
        <p style={styles.subtitle}>Multi-dimensional pricing profile per state</p>
      </div>

      <div style={styles.controls}>
        <span style={styles.sortLabel}>Sort by:</span>
        <select
          style={styles.sortSelect}
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div style={styles.grid}>
        {sortedStates.map((stateData) => {
          const isHovered = hoveredCard === stateData.state;
          return (
            <div
              key={stateData.state}
              style={{
                ...styles.card,
                ...(isHovered ? styles.cardHover : {}),
              }}
              onMouseEnter={(e) => handleMouseEnter(e, stateData)}
              onMouseLeave={handleMouseLeave}
            >
              <div style={styles.cardHeader}>
                <span style={styles.stateAbbr}>{stateData.state}</span>
                <span style={styles.badge}>
                  {stateData.addressCount} {stateData.addressCount === 1 ? 'addr' : 'addrs'}
                </span>
              </div>

              <div style={styles.svgContainer}>
                <RadarChart
                  metrics={stateData.metrics}
                  color={stateData.color || '#2563eb'}
                />
              </div>

              <div style={styles.metricsGrid}>
                {RAW_METRIC_DISPLAY.slice(0, 4).map((m) => (
                  <div key={m.key} style={styles.metricItem}>
                    <span style={styles.metricLabel}>{m.label}</span>
                    <span style={styles.metricValue}>
                      {formatMetric(m.key, stateData.rawMetrics?.[m.key])}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={styles.tooltipTitle}>
            {tooltip.stateData.state} — {tooltip.stateData.addressCount} addresses
          </div>
          {RAW_METRIC_DISPLAY.map((m) => (
            <div key={m.key} style={styles.tooltipRow}>
              <span style={styles.tooltipLabel}>{m.label}:</span>
              <span style={styles.tooltipValue}>
                {formatMetric(m.key, tooltip.stateData.rawMetrics?.[m.key])}
              </span>
            </div>
          ))}
          <div
            style={{
              marginTop: '8px',
              borderTop: '1px solid #374151',
              paddingTop: '6px',
              fontSize: '11px',
              color: '#9ca3af',
            }}
          >
            Normalized scores (0-1):
          </div>
          {AXES.map((axis) => (
            <div key={axis.key} style={styles.tooltipRow}>
              <span style={styles.tooltipLabel}>{axis.label}:</span>
              <span style={styles.tooltipValue}>
                {(tooltip.stateData.metrics?.[axis.key] ?? 0).toFixed(2)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
